import React, { useEffect, useState, useCallback } from 'react';
import api from '../../utils/axios';
import { toast } from 'sonner';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Checkbox } from '../../components/ui/checkbox';
import { useAuth } from '../../contexts/AuthContext';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from '../../components/ui/dialog';
import {
  Inbox, RefreshCw, Loader2, CheckCircle2, XCircle, Gauge, AlertTriangle, Send, ShieldAlert,
  Landmark, Building2, CalendarDays, Coins, Star, List, LayoutGrid,
} from 'lucide-react';

const fmtMoney = (n) =>
  (n || n === 0)
    ? new Intl.NumberFormat('es-AR', { style: 'currency', currency: 'ARS', maximumFractionDigits: 0 }).format(n)
    : '—';

const digits = (v) => String(v || '').replace(/[^0-9]/g, '');

const fmtDT = (s) => (s ? String(s).slice(0, 16).replace('T', ' ') : '—');

const tierColor = (t) => (t === 'Alto' ? 'text-emerald-600' : t === 'Medio' ? 'text-amber-600' : t === 'Bajo' ? 'text-rose-600' : 'text-slate-400');

const sitColor = (s) => (s == null ? 'text-slate-400' : s <= 1 ? 'text-emerald-600' : s === 2 ? 'text-amber-600' : 'text-rose-600');

const FUENTES = [
  { key: 'bcra', label: 'BCRA' },
  { key: 'pyp', label: 'PyP' },
  { key: 'nosis', label: 'Nosis' },
  { key: 'afip', label: 'AFIP' },
];

const DictamenBadge = ({ d }) =>
  d === 'Aprobar'
    ? <Badge className="bg-emerald-100 text-emerald-700 hover:bg-emerald-100 border-0"><CheckCircle2 className="h-3 w-3 mr-1" />Aprobar</Badge>
    : d === 'Rechazar'
      ? <Badge className="bg-rose-100 text-rose-700 hover:bg-rose-100 border-0"><XCircle className="h-3 w-3 mr-1" />Rechazar</Badge>
      : null;

const Solicitudes = () => {
  const { user } = useAuth();
  const canWrite = ['administrador', 'admin', 'superadmin', 'supervisor'].includes((user?.rol || '').toLowerCase());

  const [sols, setSols] = useState([]);
  const [loading, setLoading] = useState(true);
  const [previo, setPrevio] = useState({});           // cuit -> {bcra, afip}
  const [scrapedAt, setScrapedAt] = useState(null);
  const [cached, setCached] = useState(false);
  const [evals, setEvals] = useState({});             // id -> {dictamen, score, score_tier, estimacion, decision_id}
  const [evalLoading, setEvalLoading] = useState({}); // id -> bool
  const [acciones, setAcciones] = useState({});       // id -> 'aprobar' | 'rechazar'
  const [selected, setSelected] = useState(new Set());
  const [srcSel, setSrcSel] = useState({ bcra: true, pyp: true, nosis: true, afip: true });
  const [view, setView] = useState(() => localStorage.getItem('solic_view') || 'tabla');

  const [confirm, setConfirm] = useState(null);       // { items:[{id,cuit,nombre,accion}] }
  const [processing, setProcessing] = useState(false);
  const [resultados, setResultados] = useState(null); // { resultados:[...], procesados, errores }

  const setViewP = (v) => { setView(v); localStorage.setItem('solic_view', v); };

  const load = useCallback(async (refrescar = false) => {
    setLoading(true);
    setSelected(new Set());
    try {
      const r = await api.get('/motor/solicitudes', { params: { refrescar } });
      const lista = r.data?.solicitudes || [];
      setSols(lista);
      setPrevio(r.data?.previo || {});
      setScrapedAt(r.data?.scraped_at || null);
      setCached(!!r.data?.cached);
      const ev = {}; const acc = {};
      lista.forEach((s) => {
        if (s.evaluacion) {
          ev[s.id] = s.evaluacion;
          acc[s.id] = s.evaluacion.dictamen === 'Aprobar' ? 'aprobar' : 'rechazar';
        }
      });
      setEvals(ev);
      setAcciones(acc);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'No se pudieron cargar las solicitudes de Waya');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(false); }, []); // eslint-disable-line

  const toggleSrc = (k) => setSrcSel((p) => ({ ...p, [k]: !p[k] }));

  const evaluar = async (sol) => {
    const c = digits(sol.cuit);
    if (c.length !== 11) { toast.error('CUIT inválido'); return; }
    const fuentes = FUENTES.map((f) => f.key).filter((k) => srcSel[k]);
    if (fuentes.length === 0) { toast.error('Elegí al menos una fuente para consultar'); return; }
    setEvalLoading((p) => ({ ...p, [sol.id]: true }));
    try {
      const r = await api.post('/motor/evaluar', { cuit: c, fuentes });
      const d = r.data;
      setEvals((p) => ({ ...p, [sol.id]: { dictamen: d.dictamen, score: d.score, score_tier: d.score_tier, estimacion: d.estimacion, decision_id: d.decision_id, motivos: d.motivos } }));
      const accion = d.dictamen === 'Aprobar' ? 'aprobar' : 'rechazar';
      setAcciones((p) => ({ ...p, [sol.id]: accion }));
      setSelected((p) => new Set(p).add(sol.id));
      toast.success(`${sol.nombre} ${sol.apellido}: ${d.dictamen}${d.score != null ? ` · score ${d.score}` : ''}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'No se pudo evaluar el CUIT');
    } finally {
      setEvalLoading((p) => ({ ...p, [sol.id]: false }));
    }
  };

  const [evaluandoLote, setEvaluandoLote] = useState(false);
  const [evalProgreso, setEvalProgreso] = useState(null); // {done, total}

  const evaluarSeleccionadas = async () => {
    const fuentes = FUENTES.map((f) => f.key).filter((k) => srcSel[k]);
    if (fuentes.length === 0) { toast.error('Elegí al menos una fuente para consultar'); return; }
    const lista = sols.filter((s) => selected.has(s.id) && digits(s.cuit).length === 11);
    if (lista.length === 0) { toast.error('Seleccioná al menos una solicitud con CUIT válido'); return; }
    setEvaluandoLote(true);
    setEvalProgreso({ done: 0, total: lista.length });
    let ok = 0; let err = 0;
    for (let i = 0; i < lista.length; i++) {
      const sol = lista[i];
      const c = digits(sol.cuit);
      setEvalLoading((p) => ({ ...p, [sol.id]: true }));
      try {
        // eslint-disable-next-line no-await-in-loop
        const r = await api.post('/motor/evaluar', { cuit: c, fuentes });
        const d = r.data;
        setEvals((p) => ({ ...p, [sol.id]: { dictamen: d.dictamen, score: d.score, score_tier: d.score_tier, estimacion: d.estimacion, decision_id: d.decision_id, motivos: d.motivos } }));
        setAcciones((p) => ({ ...p, [sol.id]: d.dictamen === 'Aprobar' ? 'aprobar' : 'rechazar' }));
        ok++;
      } catch (e) {
        err++;
      } finally {
        setEvalLoading((p) => ({ ...p, [sol.id]: false }));
        setEvalProgreso({ done: i + 1, total: lista.length });
      }
    }
    setEvaluandoLote(false);
    setEvalProgreso(null);
    if (err === 0) toast.success(`Evaluadas ${ok} solicitud(es) con las fuentes elegidas`);
    else toast.error(`Evaluadas ${ok} · ${err} con error`);
  };

  const setAccion = (id, accion) => {
    setAcciones((p) => {
      const next = { ...p };
      if (next[id] === accion) { delete next[id]; } else { next[id] = accion; }
      return next;
    });
    setSelected((p) => { const n = new Set(p); n.add(id); return n; });
  };

  const toggleSelect = (id) => {
    setSelected((p) => {
      const n = new Set(p);
      if (n.has(id)) n.delete(id); else n.add(id);
      return n;
    });
  };

  const allSelected = sols.length > 0 && selected.size === sols.length;
  const toggleSelectAll = () => {
    setSelected(allSelected ? new Set() : new Set(sols.map((s) => s.id)));
  };

  const aplicarLoteAccion = (accion) => {
    const items = sols
      .filter((s) => selected.has(s.id))
      .map((s) => ({ id: s.id, cuit: s.cuit, nombre: `${s.nombre} ${s.apellido}`.trim(), accion }));
    if (items.length === 0) { toast.error('Seleccioná al menos una solicitud'); return; }
    setAcciones((prev) => { const n = { ...prev }; items.forEach((i) => { n[i.id] = accion; }); return n; });
    setConfirm({ items });
  };

  const abrirConfirmIndividual = (sol) => {
    if (!acciones[sol.id]) { toast.error('Elegí Aprobar o Rechazar primero'); return; }
    setConfirm({ items: [{ id: sol.id, cuit: sol.cuit, nombre: `${sol.nombre} ${sol.apellido}`.trim(), accion: acciones[sol.id] }] });
  };

  const ejecutar = async () => {
    if (!confirm?.items?.length) return;
    setProcessing(true);
    try {
      const payload = { acciones: confirm.items.map((i) => ({ id: i.id, accion: i.accion, cuit: digits(i.cuit) })) };
      const r = await api.post('/motor/solicitudes/procesar', payload);
      setConfirm(null);
      setResultados({ ...r.data, items: confirm.items });
      if (r.data?.errores > 0) {
        toast.warning(`${r.data.procesados - r.data.errores} OK · ${r.data.errores} con error`);
      } else {
        toast.success(`${r.data.procesados} solicitud(es) procesada(s) en Waya`);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'No se pudo procesar en Waya');
    } finally {
      setProcessing(false);
    }
  };

  const cerrarResultados = () => {
    setResultados(null);
    load(true);
  };

  // ── Celdas compartidas entre vista Tabla y Tarjetas ────────────────────────
  const bcraCell = (s) => {
    const bcra = (previo[digits(s.cuit)] || {}).bcra;
    if (!bcra) return <span className="text-slate-400">consultando…</span>;
    if (!bcra.ok) return <span className="text-slate-400" data-testid={`solic-bcra-${s.id}`}>{bcra.error || '—'}</span>;
    if (bcra.sin_registros) return <span className="text-emerald-600" data-testid={`solic-bcra-${s.id}`}>sin registros</span>;
    return (
      <span className={sitColor(bcra.sitMax)} data-testid={`solic-bcra-${s.id}`}>
        sit. {bcra.sitMax ?? '—'}{bcra.peor_situacion_label ? ` (${bcra.peor_situacion_label})` : ''} · {fmtMoney(bcra.total_deuda)}
      </span>
    );
  };

  const afipCell = (s) => {
    const afip = (previo[digits(s.cuit)] || {}).afip || {};
    return (
      <span className={afip.ok ? 'text-slate-600 dark:text-slate-300' : 'text-slate-400'} data-testid={`solic-afip-${s.id}`}>
        {afip.ok ? (afip.estado_clave || '—') : (afip.error || '—')}
      </span>
    );
  };

  const motorCell = (s) => {
    const ev = evals[s.id];
    if (!ev) {
      return (
        <Button variant="outline" size="sm" onClick={() => evaluar(s)} disabled={!!evalLoading[s.id]} data-testid={`solic-evaluar-${s.id}`}>
          {evalLoading[s.id] ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Gauge className="h-4 w-4 mr-1" />}
          Evaluar
        </Button>
      );
    }
    return (
      <div className="flex items-center gap-2 flex-wrap">
        <DictamenBadge d={ev.dictamen} />
        <span className={`text-sm font-semibold ${tierColor(ev.score_tier)}`}>{ev.score ?? '—'} <span className="text-[10px] font-normal">{ev.score_tier || ''}</span></span>
        {ev.estimacion?.monto_maximo > 0 && (
          <span className="text-xs text-slate-400 flex items-center gap-1"><Coins className="h-3 w-3" /> {fmtMoney(ev.estimacion.monto_maximo)}</span>
        )}
      </div>
    );
  };

  const accionCell = (s) => {
    if (!canWrite) return <span className="text-xs text-slate-400">Solo lectura</span>;
    const acc = acciones[s.id];
    const ev = evals[s.id];
    return (
      <div className="flex items-center gap-1.5 justify-end">
        {ev && (
          <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-slate-500" onClick={() => evaluar(s)} disabled={!!evalLoading[s.id]} data-testid={`solic-reevaluar-${s.id}`} title="Reevaluar">
            <RefreshCw className={`h-3.5 w-3.5 ${evalLoading[s.id] ? 'animate-spin' : ''}`} />
          </Button>
        )}
        <div className="inline-flex rounded-lg border border-slate-200 dark:border-slate-700 overflow-hidden">
          <button
            onClick={() => setAccion(s.id, 'aprobar')}
            className={`px-2.5 py-1 text-xs font-medium transition-colors ${acc === 'aprobar' ? 'bg-emerald-600 text-white' : 'text-slate-600 dark:text-slate-300 hover:bg-slate-100'}`}
            data-testid={`solic-accion-aprobar-${s.id}`}
          >Aprobar</button>
          <button
            onClick={() => setAccion(s.id, 'rechazar')}
            className={`px-2.5 py-1 text-xs font-medium border-l border-slate-200 dark:border-slate-700 transition-colors ${acc === 'rechazar' ? 'bg-rose-600 text-white' : 'text-slate-600 dark:text-slate-300 hover:bg-slate-100'}`}
            data-testid={`solic-accion-rechazar-${s.id}`}
          >Rechazar</button>
        </div>
        <Button variant="ghost" size="sm" className="h-7 text-xs text-teal-600 hover:text-teal-700" onClick={() => abrirConfirmIndividual(s)} disabled={!acc} data-testid={`solic-aplicar-${s.id}`}>
          <Send className="h-3.5 w-3.5 mr-1" /> Aplicar
        </Button>
      </div>
    );
  };

  return (
    <div className="p-6 lg:p-8 pt-20 sm:pt-6 lg:pt-8 max-w-6xl mx-auto" data-testid="solicitudes">
      {/* Header */}
      <div className="mb-5 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div className="flex items-center gap-3">
          <div className="p-2.5 rounded-xl bg-teal-500/10"><Inbox className="h-6 w-6 text-teal-600" /></div>
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-slate-900 dark:text-white">Solicitudes de Préstamo</h1>
            <p className="text-sm text-slate-500">Préstamos solicitados en Waya · {sols.length} pendiente(s)</p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          {scrapedAt && (
            <p className="text-[11px] text-slate-400 hidden sm:block text-right" data-testid="solic-scraped-at">
              {cached ? 'Guardado' : 'Actualizado'}: {fmtDT(scrapedAt)}
            </p>
          )}
          <Button variant="outline" size="sm" onClick={() => load(true)} disabled={loading} data-testid="solic-refresh-btn">
            {loading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <RefreshCw className="h-4 w-4 mr-2" />}
            Actualizar
          </Button>
        </div>
      </div>

      {/* Selección de fuentes para evaluar */}
      <Card className="p-3 mb-4 border-slate-200 dark:border-slate-800">
        <div className="flex flex-wrap items-center gap-x-5 gap-y-2">
          <span className="text-xs font-medium uppercase tracking-wider text-slate-400 flex items-center gap-1.5"><Gauge className="h-3.5 w-3.5" /> Fuentes al evaluar</span>
          {FUENTES.map((f) => (
            <label key={f.key} className="flex items-center gap-2 text-sm text-slate-600 dark:text-slate-300 cursor-pointer select-none">
              <Checkbox checked={srcSel[f.key]} onCheckedChange={() => toggleSrc(f.key)} data-testid={`solic-fuente-${f.key}`} />
              {f.label}
            </label>
          ))}
          <span className="text-[11px] text-slate-400 ml-auto hidden md:block">Destildá PyP/Nosis para evaluar gratis (BCRA/AFIP).</span>
        </div>
      </Card>

      {!canWrite && (
        <div className="mb-4 flex items-center gap-2 rounded-lg border border-amber-200 bg-amber-50/60 px-3 py-2 text-sm text-amber-700" data-testid="solic-readonly-note">
          <ShieldAlert className="h-4 w-4 shrink-0" />
          Sólo Administradores y Supervisores pueden aprobar o rechazar en Waya. Podés evaluar en modo lectura.
        </div>
      )}

      {loading ? (
        <div className="text-center text-slate-500 py-16"><Loader2 className="h-6 w-6 animate-spin mx-auto" /></div>
      ) : sols.length === 0 ? (
        <div className="text-center text-slate-400 py-16" data-testid="solic-empty">
          <Inbox className="h-10 w-10 mx-auto mb-3 opacity-40" />
          No hay solicitudes de préstamo pendientes en Waya.
        </div>
      ) : (
        <>
          {/* Toolbar: seleccionar todo + toggle de vista */}
          <div className="flex items-center gap-3 mb-3 px-1">
            <label className="flex items-center gap-2 text-sm text-slate-600 dark:text-slate-300 cursor-pointer select-none">
              <Checkbox checked={allSelected} onCheckedChange={toggleSelectAll} data-testid="solic-check-all" />
              Seleccionar todo
            </label>
            {selected.size > 0 && <span className="text-xs text-slate-400">{selected.size} seleccionada(s)</span>}
            <div className="ml-auto inline-flex rounded-lg border border-slate-200 dark:border-slate-700 overflow-hidden" data-testid="solic-view-toggle">
              <button
                onClick={() => setViewP('tabla')}
                className={`flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium transition-colors ${view === 'tabla' ? 'bg-teal-600 text-white' : 'text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-800'}`}
                data-testid="solic-view-tabla"
              ><List className="h-3.5 w-3.5" /> Tabla</button>
              <button
                onClick={() => setViewP('tarjetas')}
                className={`flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium border-l border-slate-200 dark:border-slate-700 transition-colors ${view === 'tarjetas' ? 'bg-teal-600 text-white' : 'text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-800'}`}
                data-testid="solic-view-tarjetas"
              ><LayoutGrid className="h-3.5 w-3.5" /> Tarjetas</button>
            </div>
          </div>

          {/* ── Vista TABLA ── */}
          {view === 'tabla' && (
            <Card className="overflow-hidden border-slate-200 dark:border-slate-800" data-testid="solic-tabla">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-slate-50 dark:bg-slate-800/60 text-slate-500">
                    <tr>
                      <th className="w-10 px-3 py-2.5"></th>
                      <th className="text-left px-3 py-2.5 font-medium">Solicitante</th>
                      <th className="text-left px-3 py-2.5 font-medium whitespace-nowrap">Fecha / Frec.</th>
                      <th className="text-right px-3 py-2.5 font-medium">Monto</th>
                      <th className="text-center px-3 py-2.5 font-medium">Cuotas</th>
                      <th className="text-left px-3 py-2.5 font-medium">BCRA</th>
                      <th className="text-left px-3 py-2.5 font-medium">AFIP</th>
                      <th className="text-left px-3 py-2.5 font-medium">Motor</th>
                      <th className="text-right px-3 py-2.5 font-medium">Acción</th>
                    </tr>
                  </thead>
                  <tbody>
                    {sols.map((s) => {
                      const isSel = selected.has(s.id);
                      return (
                        <tr
                          key={s.id}
                          className={`border-t border-slate-100 dark:border-slate-800 ${isSel ? 'bg-teal-50/50 dark:bg-teal-900/10' : 'hover:bg-slate-50 dark:hover:bg-slate-800/40'}`}
                          data-testid={`solic-row-${s.id}`}
                        >
                          <td className="px-3 py-2.5 align-top">
                            <Checkbox checked={isSel} onCheckedChange={() => toggleSelect(s.id)} data-testid={`solic-check-${s.id}`} />
                          </td>
                          <td className="px-3 py-2.5">
                            <div className="flex items-center gap-1.5">
                              <span className="font-medium text-slate-800 dark:text-slate-100">{s.nombre} {s.apellido}</span>
                              {s.afiliado === 'Si' && <Star className="h-3 w-3 text-teal-500" />}
                            </div>
                            <span className="font-mono text-xs text-slate-500">{s.cuit}</span>
                          </td>
                          <td className="px-3 py-2.5 text-slate-600 dark:text-slate-300 whitespace-nowrap">
                            {s.fecha}
                            <span className="block text-xs text-slate-400">{s.frecuencia}</span>
                          </td>
                          <td className="px-3 py-2.5 text-right font-semibold text-slate-800 dark:text-slate-100 whitespace-nowrap">{s.monto_texto || fmtMoney(s.monto)}</td>
                          <td className="px-3 py-2.5 text-center text-slate-600 dark:text-slate-300 whitespace-nowrap">
                            {s.cuotas}
                            <span className="block text-xs text-slate-400">{s.plazo}</span>
                          </td>
                          <td className="px-3 py-2.5 text-xs">{bcraCell(s)}</td>
                          <td className="px-3 py-2.5 text-xs">{afipCell(s)}</td>
                          <td className="px-3 py-2.5">{motorCell(s)}</td>
                          <td className="px-3 py-2.5">{accionCell(s)}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </Card>
          )}

          {/* ── Vista TARJETAS ── */}
          {view === 'tarjetas' && (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4" data-testid="solic-tarjetas">
              {sols.map((s) => {
                const isSel = selected.has(s.id);
                return (
                  <Card
                    key={s.id}
                    className={`p-4 border transition-colors ${isSel ? 'border-teal-400 ring-1 ring-teal-300 dark:border-teal-600' : 'border-slate-200 dark:border-slate-800'}`}
                    data-testid={`solic-row-${s.id}`}
                  >
                    <div className="flex items-start gap-3">
                      <Checkbox className="mt-1" checked={isSel} onCheckedChange={() => toggleSelect(s.id)} data-testid={`solic-check-${s.id}`} />
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2 flex-wrap">
                          <p className="font-semibold text-slate-800 dark:text-slate-100 truncate">{s.nombre} {s.apellido}</p>
                          {s.afiliado === 'Si' && <Badge className="bg-teal-100 text-teal-700 border-0 text-[10px]"><Star className="h-2.5 w-2.5 mr-0.5" />Afiliado</Badge>}
                        </div>
                        <p className="font-mono text-xs text-slate-500">{s.cuit}</p>
                        <p className="text-[11px] text-slate-400 flex items-center gap-1"><CalendarDays className="h-3 w-3" /> {s.fecha} · {s.frecuencia}</p>
                      </div>
                      <div className="text-right shrink-0">
                        <p className="text-lg font-bold text-slate-800 dark:text-slate-100 whitespace-nowrap">{s.monto_texto || fmtMoney(s.monto)}</p>
                        <p className="text-[11px] text-slate-400">{s.cuotas} cuotas · {s.plazo}</p>
                      </div>
                    </div>

                    <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-2">
                      <div className="flex items-start gap-2 rounded-lg bg-slate-50 dark:bg-slate-800/60 px-2.5 py-2 text-xs">
                        <Landmark className="h-3.5 w-3.5 mt-0.5 text-slate-400 shrink-0" />
                        <span className="min-w-0">BCRA: {bcraCell(s)}</span>
                      </div>
                      <div className="flex items-start gap-2 rounded-lg bg-slate-50 dark:bg-slate-800/60 px-2.5 py-2 text-xs">
                        <Building2 className="h-3.5 w-3.5 mt-0.5 text-slate-400 shrink-0" />
                        <span className="min-w-0">AFIP: {afipCell(s)}</span>
                      </div>
                    </div>

                    <div className="mt-3 pt-3 border-t border-slate-100 dark:border-slate-800 flex items-center justify-between gap-3 flex-wrap">
                      <div>{motorCell(s)}</div>
                      <div>{accionCell(s)}</div>
                    </div>
                  </Card>
                );
              })}
            </div>
          )}
        </>
      )}

      {/* Barra flotante de lote */}
      {selected.size > 0 && (
        <div className="fixed bottom-4 sm:bottom-6 left-1/2 -translate-x-1/2 z-40 w-[calc(100vw-1rem)] sm:w-auto max-w-2xl flex flex-col sm:flex-row items-stretch sm:items-center gap-2 sm:gap-3 rounded-2xl sm:rounded-full border border-slate-200 dark:border-slate-700 bg-white/95 dark:bg-slate-900/95 backdrop-blur px-4 sm:px-5 py-3 shadow-lg" data-testid="solic-lote-bar">
          <span className="text-sm text-center sm:text-left text-slate-600 dark:text-slate-300 whitespace-nowrap">
            {evaluandoLote && evalProgreso ? `Evaluando ${evalProgreso.done}/${evalProgreso.total}…` : `${selected.size} seleccionada(s)`}
          </span>
          <div className="flex items-center gap-2">
            <Button size="sm" variant="outline" className="flex-1 sm:flex-none rounded-full border-teal-300 text-teal-700 hover:bg-teal-50 dark:text-teal-300" onClick={evaluarSeleccionadas} disabled={evaluandoLote} data-testid="solic-lote-evaluar-btn">
              {evaluandoLote ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Gauge className="h-4 w-4 mr-1.5" />} Evaluar<span className="hidden sm:inline">&nbsp;seleccionadas</span>
            </Button>
            {canWrite && (
              <>
                <Button size="sm" className="flex-1 sm:flex-none bg-emerald-600 hover:bg-emerald-700 text-white rounded-full" onClick={() => aplicarLoteAccion('aprobar')} disabled={evaluandoLote} data-testid="solic-lote-aprobar-btn">
                  <CheckCircle2 className="h-4 w-4 mr-1.5" /> Aprobar<span className="hidden sm:inline">&nbsp;seleccionadas</span>
                </Button>
                <Button size="sm" className="flex-1 sm:flex-none bg-rose-600 hover:bg-rose-700 text-white rounded-full" onClick={() => aplicarLoteAccion('rechazar')} disabled={evaluandoLote} data-testid="solic-lote-rechazar-btn">
                  <XCircle className="h-4 w-4 mr-1.5" /> Rechazar<span className="hidden sm:inline">&nbsp;seleccionadas</span>
                </Button>
              </>
            )}
          </div>
        </div>
      )}

      {/* Confirmación */}
      <Dialog open={!!confirm} onOpenChange={(o) => { if (!o && !processing) setConfirm(null); }}>
        <DialogContent className="max-w-lg" data-testid="solic-confirm-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><AlertTriangle className="h-5 w-5 text-amber-500" /> Confirmar escritura en Waya</DialogTitle>
            <DialogDescription>
              Estas acciones se ejecutan <strong>de verdad</strong> en el portal de Waya (producción) y no se pueden deshacer desde acá. Revisá antes de confirmar.
            </DialogDescription>
          </DialogHeader>
          <div className="max-h-72 overflow-y-auto space-y-2 my-2">
            {confirm?.items?.map((i) => (
              <div key={i.id} className="flex items-center justify-between gap-2 rounded-lg border border-slate-200 dark:border-slate-700 px-3 py-2" data-testid={`solic-confirm-item-${i.id}`}>
                <div className="min-w-0">
                  <p className="text-sm font-medium text-slate-800 dark:text-slate-100 truncate">{i.nombre}</p>
                  <p className="font-mono text-xs text-slate-500">{i.cuit}</p>
                </div>
                {i.accion === 'aprobar'
                  ? <Badge className="bg-emerald-100 text-emerald-700 border-0"><CheckCircle2 className="h-3 w-3 mr-1" />Aprobar</Badge>
                  : <Badge className="bg-rose-100 text-rose-700 border-0"><XCircle className="h-3 w-3 mr-1" />Rechazar</Badge>}
              </div>
            ))}
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setConfirm(null)} disabled={processing} data-testid="solic-cancel-btn">Cancelar</Button>
            <Button className="bg-teal-600 hover:bg-teal-700 text-white" onClick={ejecutar} disabled={processing} data-testid="solic-confirm-btn">
              {processing ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Send className="h-4 w-4 mr-2" />}
              Confirmar y enviar ({confirm?.items?.length || 0})
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Resultados */}
      <Dialog open={!!resultados} onOpenChange={(o) => { if (!o) cerrarResultados(); }}>
        <DialogContent className="max-w-lg" data-testid="solic-resultados-dialog">
          <DialogHeader>
            <DialogTitle>Resultado del envío a Waya</DialogTitle>
            <DialogDescription>
              {resultados ? `${resultados.procesados - resultados.errores} correcta(s) · ${resultados.errores} con error` : ''}
            </DialogDescription>
          </DialogHeader>
          <div className="max-h-72 overflow-y-auto space-y-2 my-2">
            {(resultados?.resultados || []).map((r) => {
              const it = (resultados.items || []).find((x) => String(x.id) === String(r.id)) || {};
              return (
                <div key={r.id} className="flex items-center justify-between gap-2 rounded-lg border border-slate-200 dark:border-slate-700 px-3 py-2" data-testid={`solic-resultado-${r.id}`}>
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-slate-800 dark:text-slate-100 truncate">{it.nombre || `Préstamo ${r.id}`}</p>
                    <p className="text-xs text-slate-500 truncate">{r.estado === 2 ? 'Aprobar' : 'Rechazar'} · {r.message}</p>
                  </div>
                  {r.success
                    ? <CheckCircle2 className="h-5 w-5 text-emerald-600 shrink-0" />
                    : <XCircle className="h-5 w-5 text-rose-600 shrink-0" />}
                </div>
              );
            })}
          </div>
          <DialogFooter>
            <Button className="bg-teal-600 hover:bg-teal-700 text-white" onClick={cerrarResultados} data-testid="solic-resultados-close">Cerrar y actualizar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Solicitudes;
